import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { SearchResultsCardGeneric } from '@/design-system/atoms/SearchResultsCards/SearchResultsCardGeneric';
import { SearchResultsCardLocation } from '@/design-system/atoms/SearchResultsCards/SearchResultsCardLocation';
import { SearchResultsCardTeam } from '@/design-system/atoms/SearchResultsCards/SearchResultsCardTeam';
import { SearchResultsCardTrial } from '@/design-system/atoms/SearchResultsCards/SearchResultsCardTrial';

import styles from './SearchResultsList.module.scss';

/**
 * The SearchResultsList component displays search results.
 * @param {object} props - The component props
 * @param {string} props.className - Additional class name
 * @param {array} props.results - The search results
 * @param {number} props.firstResult - The index of the first search result of the current page
 * @param {ref} props.firstResultRef - The first search result of the current page
 * @returns {React.Component} The search results component
 */
const SearchResultsList = ({
    analytics,
    className,
    results,
    firstResult,
    firstResultRef,
    ...props
}) => {
    /**
     * Get the proper search card component for the result type.
     * @param {string} type
     * @returns {React.Component} The proper search card for this result type
     */
    const getComponent = (type) => {
        switch (type) {
            case 'location':
                return SearchResultsCardLocation;
            case 'provider':
                return SearchResultsCardTeam;
            case 'trial':
                return SearchResultsCardTrial;
            default:
                return SearchResultsCardGeneric;
        }
    };

    return (
        <ul className={classnames(styles['search-results'], className)} {...props}>
            {results?.map((result, index) => {
                const Component = getComponent(result.type);

                return (
                    <li className={styles['search-results__item']} key={index}>
                        <Component
                            // Set the ref for the first result of the current page
                            ref={index + 1 === firstResult ? firstResultRef : null}
                            {...result}
                            analytics={{ ...analytics, searchPosition: index + 1 }}
                        />
                    </li>
                );
            })}
        </ul>
    );
};

SearchResultsList.propTypes = {
    className: PropTypes.string,
    results: PropTypes.array,
};

SearchResultsList.defaultProps = {
    results: [],
    firstResult: 1,
};

export default SearchResultsList;
